import { createTypedAsyncThunk, withOrgId } from "../utils/actions";
import * as API from "@/data/api/naeras";
import {
  RequestParams,
  TNaerasAdjustPayload,
  TNaerasBid,
  TNaerasBidSearchParams,
  TNaerasBidsResponse,
  TNaerasCalculatePayload,
  TNaerasCalculateResponse,
  TNaerasCalendar,
  TNaerasDashboard,
  TNaerasFinalizePayload,
  TNaerasFinalizeResponse,
  TNaerasImportResponse,
  TNaerasJob,
  TNaerasJobRun,
  TNaerasJobRunsResponse,
  TNaerasPeziWaterDay,
  TNaerasPeziWaterParams,
  TNaerasPoolState,
  TNaerasPoolUpdatePayload,
  TNaerasSettings,
} from "@/data/types";
import { State } from "../state/state";

export const getNaerasCalendarAsync = createTypedAsyncThunk<
  TNaerasCalendar,
  { params?: RequestParams & { from?: string; to?: string } } | void,
  { state: State }
>("naeras/getCalendar", async (args, { getState }) => {
  return API.getNaerasCalendar(withOrgId(getState, args?.params ?? {}));
});

export const getNaerasBidsAsync = createTypedAsyncThunk<
  TNaerasBidsResponse,
  TNaerasBidSearchParams,
  { state: State }
>("naeras/getBids", async (params, { getState }) => {
  return API.getNaerasBids(withOrgId(getState, params));
});

/**
 * A day that has never been calculated answers 404 — an expected outcome the
 * page shows inline, so callers pass `silent` to keep it out of the global
 * error toast.
 */
export const getNaerasBidAsync = createTypedAsyncThunk<
  TNaerasBid,
  { targetDate: string; params?: RequestParams; silent?: boolean },
  { state: State }
>("naeras/getBid", async ({ targetDate, params = {} }, { getState }) => {
  return API.getNaerasBid(targetDate, withOrgId(getState, params));
});

/**
 * The write thunks all take `silent`, because the screens report their failures
 * themselves: these calls are atomic, and "nothing was saved" is the part the
 * operator needs — the global toast only shows the server's sentence.
 */
export const calculateNaerasBidsAsync = createTypedAsyncThunk<
  TNaerasCalculateResponse,
  { params?: RequestParams; payload: TNaerasCalculatePayload; silent?: boolean },
  { state: State }
>("naeras/calculateBids", async ({ params = {}, payload }, { getState }) => {
  return API.calculateNaerasBids(withOrgId(getState, params), payload);
});

export const adjustNaerasBidAsync = createTypedAsyncThunk<
  TNaerasBid,
  { targetDate: string; params?: RequestParams; payload: TNaerasAdjustPayload; silent?: boolean },
  { state: State }
>("naeras/adjustBid", async ({ targetDate, params = {}, payload }, { getState }) => {
  return API.adjustNaerasBid(targetDate, withOrgId(getState, params), payload);
});

export const exportNaerasBidsAsync = createTypedAsyncThunk<
  Blob,
  { dates: string[]; params?: RequestParams },
  { state: State }
>("naeras/exportBids", async ({ dates, params = {} }, { getState }) => {
  return API.exportNaerasBids(dates, withOrgId(getState, params));
});

export const importNaerasBidsAsync = createTypedAsyncThunk<
  TNaerasImportResponse,
  { file: File; params?: RequestParams; silent?: boolean },
  { state: State }
>("naeras/importBids", async ({ file, params = {} }, { getState }) => {
  return API.importNaerasBids(file, withOrgId(getState, params));
});

/**
 * Locks the given days. This is the only way a day becomes `final` from the UI:
 * uploading the Excel merely stores a draft. Re-finalizing an already-final day
 * applies its pending revision, which may also correct the latest final bid —
 * `correction.target_date` says which, and that row has to be refetched.
 */
export const finalizeNaerasBidsAsync = createTypedAsyncThunk<
  TNaerasFinalizeResponse,
  { params?: RequestParams; payload: TNaerasFinalizePayload; silent?: boolean },
  { state: State }
>("naeras/finalizeBids", async ({ params = {}, payload }, { getState }) => {
  return API.finalizeNaerasBids(withOrgId(getState, params), payload);
});

export const getNaerasJobRunsAsync = createTypedAsyncThunk<
  TNaerasJobRunsResponse,
  { params?: RequestParams & { job?: TNaerasJob; status?: string; cursor?: string } } | void,
  { state: State }
>("naeras/getJobRuns", async (args, { getState }) => {
  return API.getNaerasJobRuns(withOrgId(getState, args?.params ?? {}));
});

/**
 * The most recent runs across every job, with no job/status filter, feeding the
 * per-job status cards. Separate from the paginated history list so filtering
 * the table can't make a job that ran look like one that didn't.
 */
export const getNaerasTodayJobRunsAsync = createTypedAsyncThunk<
  TNaerasJobRunsResponse,
  { params?: RequestParams } | void,
  { state: State }
>("naeras/getTodayJobRuns", async (args, { getState }) => {
  return API.getNaerasJobRuns(
    withOrgId(getState, { page_size: 25, sort_dir: "DESC", ...(args?.params ?? {}) })
  );
});

export const getNaerasPoolAsync = createTypedAsyncThunk<
  TNaerasPoolState,
  { params?: RequestParams & { date?: string } } | void,
  { state: State }
>("naeras/getPool", async (args, { getState }) => {
  return API.getNaerasPool(withOrgId(getState, args?.params ?? {}));
});

/**
 * Pool edits answer with the pool of the day that was edited, which need not be
 * today — so the store is left alone and callers refetch today's pool after.
 * Both are silent: the edit dialog reports its own errors in place.
 */
export const updateNaerasPoolAsync = createTypedAsyncThunk<
  TNaerasPoolState,
  { payload: TNaerasPoolUpdatePayload; params?: RequestParams; silent?: boolean },
  { state: State }
>("naeras/updatePool", async ({ payload, params = {} }, { getState }) => {
  return API.updateNaerasPool(withOrgId(getState, params), payload);
});

export const revertNaerasPoolAsync = createTypedAsyncThunk<
  TNaerasPoolState,
  { date: string; params?: RequestParams; silent?: boolean },
  { state: State }
>("naeras/revertPool", async ({ date, params = {} }, { getState }) => {
  return API.revertNaerasPool(withOrgId(getState, { ...params, date }));
});

export const getNaerasJobRunAsync = createTypedAsyncThunk<
  TNaerasJobRun | null,
  { job: TNaerasJob; runDate: string; params?: RequestParams },
  { state: State }
>("naeras/getJobRun", async ({ job, runDate, params = {} }, { getState }) => {
  return API.getNaerasJobRun(job, runDate, withOrgId(getState, params));
});

/**
 * The whole dashboard. Polled, and silent: a 502 means every panel failed at
 * once, which the page reports in place rather than as a toast every minute.
 */
export const getNaerasDashboardAsync = createTypedAsyncThunk<
  TNaerasDashboard,
  { params?: RequestParams; silent?: boolean } | void,
  { state: State }
>("naeras/getDashboard", async (args, { getState }) => {
  return API.getNaerasDashboard(withOrgId(getState, args?.params ?? {}));
});

/**
 * Water from Πέζι for any range up to 366 days, for the §2.7 CSV. Not kept in
 * the store: it is fetched for one download and thrown away. The body is
 * unwrapped here so callers always get the list of days.
 */
export const getNaerasPeziWaterAsync = createTypedAsyncThunk<
  TNaerasPeziWaterDay[],
  { params?: RequestParams & TNaerasPeziWaterParams; silent?: boolean } | void,
  { state: State }
>("naeras/getPeziWater", async (args, { getState }) => {
  const response: any = await API.getNaerasPeziWater(withOrgId(getState, args?.params ?? {}));

  return Array.isArray(response) ? response : response?.days ?? response?.items ?? [];
});

export const getNaerasSettingsAsync = createTypedAsyncThunk<
  TNaerasSettings,
  { params?: RequestParams; silent?: boolean } | void,
  { state: State }
>("naeras/getSettings", async (args, { getState }) => {
  return API.getNaerasSettings(withOrgId(getState, args?.params ?? {}));
});

/**
 * Silent: the switch reports a refusal (NAERAS_WET_SEASON_LOCKED) in place and
 * puts itself back to what the server holds.
 */
export const updateNaerasSettingsAsync = createTypedAsyncThunk<
  TNaerasSettings,
  { wetSeason: boolean; params?: RequestParams; silent?: boolean },
  { state: State }
>("naeras/updateSettings", async ({ wetSeason, params = {} }, { getState }) => {
  return API.updateNaerasSettings(withOrgId(getState, params), { wet_season: wetSeason });
});
